import { clone } from 'lodash-es';
import moment from 'moment';

import { MOCK } from '../../configDebug';
import configLib from '../../configLib';
import { IndividualExtendedInfoData } from '../models/enroll';
import { TypedObject } from '../util/types';

import amsGetAllMessagesJson from './ams/getAllMessages.json';
import amsGetMessageJson from './ams/getMessage.json';
import amsGetSummaryJson from './ams/getSummary.json';
import amsNotifyUnreadCountJson from './ams/notifyUnreadCount.json';
import amsVoidResponseJson from './ams/voidResponse.json';
import authRefreshJson from './auth/refresh.json';
import authRefreshEmailNotVerifiedJson from './auth/refresh-email-not-verified.json';
import authUserPermissionsJson from './auth/userPermissions.json';
import crmAccountDetailsJson from './crm/accountDetails.json';
import crmAccountInfoJson from './crm/accountInfo.json';
import crmAccountsJson from './crm/accounts.json';
import crmBankAccountInfoJson from './crm/bankAccountInfo.json';
import crmBusinessOwnJson from './crm/businessOwn.json';
import crmCountryJson from './crm/country.json';
import crmEmploymentStatusOwnJson from './crm/employmentStatusOwn.json';
import crmFinancialQuestionnaireOwnListJson from './crm/financialQuestionnaireOwnList.json';
import crmFinancialQuestionnaireTypesJson from './crm/financialQuestionnaireTypes.json';
import crmFundingWireTransferResponseJson from './crm/fundingWireTransferResponse.json';
import crmIndividualExtendedInfoNewJson from './crm/individualExtendedInfo_new.json';
import crmIndividualExtendedInfoOnboarding11pdJson from './crm/individualExtendedInfo_onboarding-1.1-pd.json';
import crmIndividualExtendedInfoOnboarding12pdJson from './crm/individualExtendedInfo_onboarding-1.2-pd.json';
import crmIndividualExtendedInfoOnboarding21finQsJson from './crm/individualExtendedInfo_onboarding-2.1-fin-qs.json';
import crmIndividualExtendedInfoOnboarding24finQsJson from './crm/individualExtendedInfo_onboarding-2.4-fin-qs.json';
import crmIndividualExtendedInfoOnboarding35finQsJson from './crm/individualExtendedInfo_onboarding-3.5-fin-qs.json';
import crmIndividualExtendedInfoOnboardingPendingVerificationJson from './crm/individualExtendedInfo_onboarding-pending-verification.json';
import crmIndividualExtendedInfoOnboardingVerificationJson from './crm/individualExtendedInfo_onboarding-verification.json';
import crmIndividualIdentityDetailOwnJson from './crm/individualIdentityDetailOwn.json';
import crmIndividualLinkedAccountsJson from './crm/individualLinkedAccounts.json';
import crmIndividualOwnActiveRiskAssessmentJson from './crm/individualOwnActiveRiskAssessment.json';
import crmIndividualOwnLegalDeclarationsJson from './crm/individualOwnLegalDeclarations.json';
import crmLegalDeclarationListJson from './crm/legalDeclarationList.json';
import crmPaymentsJson from './crm/payments.json';
import crmPepDetailJson from './crm/pepDetail.json';
import crmPurposeOfTradingActiveJson from './crm/purposeOfTradingActive.json';
import crmSourceOfFundOwnJson from './crm/sourceOfFundOwn.json';
import fundamentalCompaniesJson from './fundamental/companies.json';
import fundamentalCompanyLogoJson from './fundamental/companyLogo.json';
import fundamentalCompanyLogoAndNameJson from './fundamental/companyLogoAndName.json';
import fundamentalCompanyLogoMD5Json from './fundamental/companyLogoMD5.json';
import fundamentalPopularStocksJson from './fundamental/popularStocks.json';
import fundamentalSearchJson from './fundamental/search.json';
import fundamentalSectorJson from './fundamental/sector.json';
import fundamentalSectorsJson from './fundamental/sectors.json';
import fundamentalStatsJson from './fundamental/stats.json';
import gatewayMdCompressed2Json from './gateway/md/compressed-2.json';
import gatewayMdInstrumentSnapshotJson from './gateway/md/instrument-snapshot.json';
import gatewayMdNbboJson from './gateway/md/nbbo.json';
import gatewayMdOhlcvRecentPricesJson from './gateway/md/ohlcv-RecentPrices.json';
import gatewayMdTradeJson from './gateway/md/trade.json';
import gatewayNewsNewsForSymbolsJson from './gateway/news/news-for-symbols.json';
import gatewayNewOrderAcceptedJson from './gateway/sor/new-order-accepted.json';
import gatewayNewOrderRejectedJson from './gateway/sor/new-order-rejected.json';
import marketDataNbboJson from './market-data/nbbo.json';
import marketDataOhlcvJson from './market-data/ohlcv.json';
import newsNewsJson from './news/news.json';
import reportingAccountSummaryJson from './reporting/accountSummary.json';
import reportingAccountTradeHistoryJson from './reporting/accountTradeHistory.json';
import reportingNetEquityComponentsJson from './reporting/netEquityComponents.json';
import reportingOpenPositionsDataJson from './reporting/openPositionsData.json';
import reportingOrderDetailJson from './reporting/orderDetail.json';
import tradingBalancesJson from './trading/balances.json';
import tradingNeworderJson from './trading/neworder.json';
import tradingOrdersJson from './trading/orders.json';
import tradingPositionsJson from './trading/positions.json';
import badRequestJson from './badRequest.json';
import emptyJson from './empty.json';
import { ExtendedInfoMockEnum } from './types';

export const badRequest = badRequestJson;
export const empty = emptyJson;


function mockAuthorise(defaultCase = null) {
  switch (MOCK.EXTENDED_INFO) {
    case ExtendedInfoMockEnum.ENROLL:
      return authRefreshEmailNotVerifiedJson;

    default:
      if (defaultCase) return defaultCase;
      return authRefreshJson;
  }
}

// Auth
const authImplementation: any = {
  authorize: mockAuthorise(),
  refresh: mockAuthorise(authRefreshJson as any),
  // common
  registerUser: { data: { id: 198 } },
  userPermissions: authUserPermissionsJson,
  confirmEmail: authRefreshJson,
  redeemSignupToken: {},
};
const authProxy = {
  get(target: any, callName: string, receiver: any) {
    if (!authImplementation[callName]) return empty;

    return authImplementation[callName];
  },
};
export const auth = new Proxy(authImplementation, authProxy);

// CRM
const crmImplementation: any = {
  accounts: crmAccountsJson,
  accountInfo: crmAccountInfoJson,
  accountDetails: crmAccountDetailsJson,
  // enroll
  sourceOfFundOwn: crmSourceOfFundOwnJson,
  country: crmCountryJson,
  employmentStatusOwn: crmEmploymentStatusOwnJson,
  businessOwn: crmBusinessOwnJson,
  purposeOfTradingActive: crmPurposeOfTradingActiveJson,
  financialQuestionnaireTypes: crmFinancialQuestionnaireTypesJson,
  financialQuestionnaireOwnList: crmFinancialQuestionnaireOwnListJson,
  legalDeclarationList: crmLegalDeclarationListJson,
  individualOwnLegalDeclarations: crmIndividualOwnLegalDeclarationsJson,
  individualOwnActiveRiskAssessment: crmIndividualOwnActiveRiskAssessmentJson,
  pepDetail: crmPepDetailJson,
  getPayments: crmPaymentsJson,
  // enroll - other partially or fully filled examples
  individualIdentityDetailOwn: crmIndividualIdentityDetailOwnJson,
  linkedAccounts: crmIndividualLinkedAccountsJson,
  detailedAccount: crmAccountDetailsJson,
  vendorBankAccount: crmBankAccountInfoJson,
  fundingWireTransferInfo: crmFundingWireTransferResponseJson,
  // complex sets
  ...(individualExtendedInfo(MOCK.EXTENDED_INFO)),
};
type MockedCall = { id: number, count: number };
let callsRegistry: TypedObject<MockedCall> = {};
const crmProxy = {
  get(target: any, callName: string, receiver: any) {
    if ([ '$$typeof', 'prototype' ].includes(callName)) return target[callName];

    if (crmImplementation[callName] === undefined && MOCK.RESPONSE_EMPTY_FALLBACK) {
      console.warn(`[CRM-MOCK] No implementation for '${callName}' - returning empty response`);
      return empty;
    }

    // initial value of callsRegistry for callName
    let isInitial = !callsRegistry[callName];
    if (isInitial) {
      callsRegistry[callName] = { id: 1, count: 1 };
      isInitial = true;
    }
    let current = callsRegistry[callName];

    // update id and count (if not initial)
    if (!isInitial) {
      if (crmImplementation[`${callName}_${current.id + 1}`]) current.id++;
      current.count++;
    }

    // logging
    const { count, id } = current;
    if (id > 1 && crmImplementation[`${callName}_${id}`]) {
      console.debug(`[CRM-MOCK] Calling '${`${callName}_${id}`}' - ${count} time(s) called`);
      return crmImplementation[`${callName}_${id}`];
    }

    console.debug(`[CRM-MOCK] Calling '${callName}' for the first time`);
    return crmImplementation[callName];
  },
};
export const crm = new Proxy(crmImplementation, crmProxy);

// File
export const file: any = {
  upload: 'some-id',
};

// Gateway
export const gateway: any = {
  cnbbo: gatewayMdCompressed2Json,
  nbbo: gatewayMdNbboJson,
  ohlcv: gatewayMdOhlcvRecentPricesJson,
  news: gatewayNewsNewsForSymbolsJson,
  newsForSymbol: gatewayNewsNewsForSymbolsJson,
  trade: gatewayMdTradeJson,
  // TODO: This case has not yet been received for NewOrderRequest - temporarily using ParentOrder
  newOrderAccepted: gatewayNewOrderAcceptedJson,
  newOrderRejected: gatewayNewOrderRejectedJson,
  instrumentSnapshot: gatewayMdInstrumentSnapshotJson,
};

// Trading
export const trading: any = {
  getOrders: tradingOrdersJson,
  getPositions: tradingPositionsJson,
  newOrder: tradingNeworderJson,
  getBalances: tradingBalancesJson,
};

// Market Data
const marketImpl: any = {
  subscribe: marketDataNbboJson,
  nbbo: marketDataNbboJson,
  ohlcv: marketDataOhlcvJson,
};
const marketProxy = {
  get(target: any, callName: string, receiver: any) {
    if (callName === 'ohlcv') {
      let tmp = clone(target[callName] ?? []);
      const item0 = clone(tmp[0]);
      if (item0) {
        item0.date = moment().format('YYYY-MM-DD'); // add current date for price info
        tmp = tmp.concat();
        tmp.push(item0);
      }
      return tmp;
    }
    return target[callName];
  },
};
export const market = new Proxy(marketImpl, marketProxy);

// Fundamental
export const fundamental: any = {
  search: fundamentalSearchJson,
  company: fundamentalCompaniesJson,
  // TODO: Delete after merging ART-1918 and use getCompanyLogoAndName for both mobile and web
  companyLogo: fundamentalCompanyLogoJson,
  companyLogoAndName: fundamentalCompanyLogoAndNameJson,
  companyLogoMD5: fundamentalCompanyLogoMD5Json,
  stats: fundamentalStatsJson,
  popularStocks: fundamentalPopularStocksJson,
  sectors: fundamentalSectorsJson,
  sector: fundamentalSectorJson,
};

// News
export const news: any = {
  news: newsNewsJson,
  newsForSymbol: newsNewsJson,
};

// Reporting
export const reporting: any = {
  getNetEquityComponents: reportingNetEquityComponentsJson,
  getAccountTradeHistory: reportingAccountTradeHistoryJson,
  getOpenPositionsData: reportingOpenPositionsDataJson,
  getAccountSummaryData: reportingAccountSummaryJson,
  getOrderDetailData: reportingOrderDetailJson,
};

// AMS

export const ams: any = {
  getAllMessages: amsGetAllMessagesJson,
  getMessage: amsGetMessageJson,
  getSummary: amsGetSummaryJson,
  voidResponse: amsVoidResponseJson,
  notifyUnreadCountResponse: amsNotifyUnreadCountJson,
};


/**
 * Mock mode for extended info. Populates data per case
 * @param caseNo Default is tradeuser logged in
 */
function individualExtendedInfo(caseNo?: ExtendedInfoMockEnum) {
  let res: TypedObject<IndividualExtendedInfoData | null>;
  switch (caseNo) {
    case ExtendedInfoMockEnum.ENROLL_1_1_PD:
      res = {
        individualExtendedInfo: crmIndividualExtendedInfoOnboarding11pdJson as any,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL_1_2_PD:
      res = {
        individualExtendedInfo: crmIndividualExtendedInfoOnboarding12pdJson as any,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL_2_1_FIN_QS:
      res = {
        individualExtendedInfo: crmIndividualExtendedInfoOnboarding21finQsJson as any,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL_2_4_SOF:
      res = {
        individualExtendedInfo: crmIndividualExtendedInfoOnboarding24finQsJson as any,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL_3_5_FIN_QS:
      res = {
        individualExtendedInfo: crmIndividualExtendedInfoOnboarding35finQsJson as any,
        individualExtendedInfo_1: crmIndividualExtendedInfoOnboardingVerificationJson as any,
        individualExtendedInfo_2: crmIndividualExtendedInfoNewJson as any,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL_VERIFICATION:
      res = {
        individualExtendedInfo: crmIndividualExtendedInfoOnboardingVerificationJson as any,
        individualExtendedInfo_2: crmIndividualExtendedInfoNewJson as any,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL_DOCUMENT_VERIFICATION:
      res = {
        individualExtendedInfo: crmIndividualExtendedInfoOnboardingPendingVerificationJson as any,
      };
      break;

    case ExtendedInfoMockEnum.NULL:
      res = {
        individualExtendedInfo: null,
      };
      break;

    case ExtendedInfoMockEnum.ENROLL:
      res = {
        individualExtendedInfo: {} as IndividualExtendedInfoData,
      };
      break;

    default:
      res = {
        individualExtendedInfo: crmIndividualExtendedInfoNewJson as any,
      };
      break;
  }

  res.individualExtendedInfo = {
    ...res.individualExtendedInfo,
    enroll_channel: configLib.isWeb ? 'W' : 'M',
  } as any;

  return res;
}
